// C4 "Leaving school": the words a student sees when their ANNA account is closing or closed.
// Shared by the closing banner (common/ClosingBanner.js), the "Finishing this year?" card on
// Results (studentResults/KeepResultsCard.js), the Sign In window and the test/booking refusals
// (chatbot/testShell/refusals.js).

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);

/**
 * "Oct 15" from 'YYYY-MM-DD' (read as a local date, never a day off) or an ISO time, with the
 * year when it isn't this year ("Oct 15, 2025"). '' when there is no readable date.
 */
export const closeDay = (value) => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
};

/**
 * The closing the student should hear about, from /auth/user/ (`leaving`), or null. Only
 * 'closing' shows anything; staff accounts are never part of this.
 */
export const closingOf = (user) => {
  if (!user || user.is_staff || user.is_superuser) return null;
  return user.leaving && user.leaving.state === 'closing' ? user.leaving : null;
};

/** The school's name for "Still at UIC?", or '' when the payload has none. */
export const schoolOf = (user, leaving = null) => String(leaving?.school_name || user?.school_name || '').trim();

/** Why it closes, in one line: the class list, or just that the guidance office is closing it. */
export const closingReasonText = (leaving, school) => {
  const reason = leaving?.reason;
  if (!reason || reason === 'not_back') {
    return school ? `You're not on ${school}'s class list this year.` : "You're not on your school's class list this year.";
  }
  return 'The guidance office is closing it.';
};

/** "Still at UIC? Tell the guidance office." */
export const stillHereText = (school) => `Still at ${school || 'your school'}? Tell the guidance office.`;

/** Why a test or a booking was turned down while the account is closing (code 'account_closing'). */
export const accountClosingText = (closesOn, lang = 'en') => {
  const day = closeDay(closesOn);
  return day
    ? tx(lang,
      `Your account closes on ${day}, so new tests and bookings are off. You can still see and save your results.`,
      `Magsasara ang account mo sa ${day}, kaya hindi ka na makakapagsimula ng bagong test o makakapag-book. Makikita at mase-save mo pa rin ang mga resulta mo.`)
    : tx(lang,
      'Your account is closing soon, so new tests and bookings are off. You can still see and save your results.',
      'Malapit nang magsara ang account mo, kaya hindi ka na makakapagsimula ng bagong test o makakapag-book. Makikita at mase-save mo pa rin ang mga resulta mo.');
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const PERSONAL_EMAIL_INVALID = 'Check the email. It should look like name@example.com.';

/**
 * What's wrong with the "Email for after you leave school" (User.personal_email), or ''. Empty is
 * fine: it's optional. Used by My profile (AccountDetails.js) and the Results card.
 */
export const personalEmailProblem = (value, schoolEmail = '') => {
  const email = String(value || '').trim().toLowerCase();
  if (!email) return '';
  if (!EMAIL.test(email)) return PERSONAL_EMAIL_INVALID;
  if (schoolEmail && email === String(schoolEmail).trim().toLowerCase()) {
    return "That's your school email. Add one you'll still use after you leave.";
  }
  return '';
};

/** The Sign In window's line for a closed account (401 with code 'account_closed'). */
export const closedSignInText = ({ closedOn, school } = {}) => {
  const day = closeDay(closedOn);
  const where = String(school || '').trim();
  return {
    title: day ? `The guidance office closed this account on ${day}.` : 'The guidance office closed this account.',
    text: `Need your results or still at ${where || 'your school'}? Ask the guidance office.`,
  };
};
